<template>
  <v-navigation-drawer
    v-model="drawerProxy"
    class="caion-nav-drawer admin-nav-drawer"
    :class="{ 'is-collapsed': collapsed }"
    :permanent="permanent"
    :temporary="!permanent"
    :width="drawerWidth"
    elevation="0"
  >
    <v-list nav density="comfortable" class="menu-list" :opened="openedGroups">
      <template v-for="item in menuItems" :key="item.title">
        <v-list-item
          v-if="!item.children"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="collapsed ? undefined : item.title"
          class="menu-item"
          rounded="0"
          @click="closeIfOverlay"
        >
          <v-tooltip v-if="collapsed" activator="parent" location="end">{{ item.title }}</v-tooltip>
        </v-list-item>

        <v-list-group v-else :value="item.title">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              :prepend-icon="item.icon"
              :title="item.title"
              class="menu-item"
              rounded="0"
            />
          </template>
          <v-list-item
            v-for="child in item.children"
            :key="child.title"
            :to="child.to"
            :prepend-icon="child.icon"
            :title="child.title"
            class="menu-item sub-item"
            rounded="0"
            @click="closeIfOverlay"
          />
        </v-list-group>
      </template>
    </v-list>

    <div v-if="adminList.length" class="admin-section">
      <div class="admin-label">
        <v-icon size="16">mdi-shield-account-outline</v-icon>
        <span>관리자 전용</span>
      </div>
      <v-list nav density="comfortable" class="menu-list admin-menu-list">
        <template v-for="item in adminList" :key="item.title">
          <v-list-item
            v-if="!item.children && item.external"
            :prepend-icon="item.icon"
            :title="collapsed ? undefined : item.title"
            class="menu-item menu-item--external"
            rounded="0"
            @click="openExternal(item)"
          >
            <v-tooltip v-if="collapsed" activator="parent" location="end">{{ item.title }}</v-tooltip>
          </v-list-item>
          <v-list-item
            v-else-if="!item.children"
            :to="item.to"
            :prepend-icon="item.icon"
            :title="collapsed ? undefined : item.title"
            class="menu-item"
            rounded="0"
            @click="closeIfOverlay"
          >
            <v-tooltip v-if="collapsed" activator="parent" location="end">{{ item.title }}</v-tooltip>
          </v-list-item>
          <v-list-group v-else :value="item.title">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :prepend-icon="item.icon"
                :title="item.title"
                class="menu-item"
                rounded="0"
              />
            </template>
            <v-list-item
              v-for="child in item.children"
              :key="child.title"
              :to="child.to"
              :prepend-icon="child.icon"
              :title="child.title"
              class="menu-item sub-item"
              rounded="0"
              @click="closeIfOverlay"
            />
          </v-list-group>
        </template>
      </v-list>
    </div>

    <!-- 슈퍼바이저(100) 전용 메뉴 그룹 -->
    <div v-if="supervisorList.length" class="supervisor-section">
      <div class="supervisor-label">
        <v-icon size="16">mdi-shield-crown-outline</v-icon>
        <span>슈퍼바이저 전용</span>
      </div>
      <v-list nav density="comfortable" class="menu-list supervisor-menu-list">
        <template v-for="item in supervisorList" :key="item.title">
          <v-list-item
            v-if="!item.children"
            :to="item.to"
            :prepend-icon="item.icon"
            :title="collapsed ? undefined : item.title"
            class="menu-item"
            rounded="0"
            @click="closeIfOverlay"
          >
            <v-tooltip v-if="collapsed" activator="parent" location="end">{{ item.title }}</v-tooltip>
          </v-list-item>

          <v-list-group v-else :value="item.title">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :prepend-icon="item.icon"
                :title="item.title"
                class="menu-item"
                rounded="0"
              />
            </template>
            <v-list-item
              v-for="child in item.children"
              :key="child.title"
              :to="child.to"
              :prepend-icon="child.icon"
              :title="child.title"
              class="menu-item sub-item"
              rounded="0"
              @click="closeIfOverlay"
            />
          </v-list-group>
        </template>
      </v-list>
    </div>

    <template v-slot:append>
      <div class="drawer-footer">
        <span class="footer-logo">CAION</span>
        <span class="footer-sub">관리자 시스템</span>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { NavItem } from '@/config/navigation';
import { ltcsAdminEnterApi } from '@/services/api';

interface Props {
  modelValue: boolean;
  permanent?: boolean;
  collapsed?: boolean;
  menuItems: NavItem[];
  adminItems?: NavItem[];
  supervisorItems?: NavItem[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const route = useRoute();

const collapsed = computed(() => !!props.collapsed);
const drawerWidth = computed(() => {
  if (!props.permanent) return 280;
  return collapsed.value ? 72 : 248;
});
const adminList = computed(() => props.adminItems ?? []);
const supervisorList = computed(() => props.supervisorItems ?? []);

const closeIfOverlay = () => {
  if (!props.permanent) drawerProxy.value = false;
};

const openExternal = async (item: NavItem) => {
  closeIfOverlay();
  if (item.external !== 'ltcs-admin') return;
  const popup = window.open('about:blank', '_blank');
  if (!popup) {
    window.alert('새 창을 열 수 없습니다. 브라우저에서 팝업을 허용해 주세요.');
    return;
  }
  popup.document.body.textContent = '함우리몰관리를 여는 중입니다.';
  try {
    const res = await ltcsAdminEnterApi.openUrl();
    const data = res.data?.data || {};
    const action = String(data.action || '');
    const userId = String(data.userId || '');
    const password = String(data.password || '');
    if (!action || !userId || !password) throw new Error('empty');
    const doc = popup.document;
    doc.body.textContent = '';
    const form = doc.createElement('form');
    form.method = 'POST';
    form.action = action;
    const add = (name: string, value: string) => {
      const input = doc.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    add('userId', userId);
    add('password', password);
    add('rememberMe', data.rememberMe ? 'true' : 'false');
    doc.body.appendChild(form);
    form.submit();
  } catch (error: any) {
    popup.close();
    window.alert(error?.response?.data?.message || '함우리몰관리를 열지 못했습니다.');
  }
};

const drawerProxy = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
});

// 현재 경로에 해당하는 메뉴 그룹을 자동으로 열기
const openedGroups = computed(() => {
  const currentPath = route.path;
  const opened: string[] = [];
  
  for (const item of [...props.menuItems, ...adminList.value, ...supervisorList.value]) {
    if (item.children) {
      const hasActiveChild = item.children.some(child => child.to === currentPath);
      if (hasActiveChild) {
        opened.push(item.title);
      }
    }
  }
  
  return opened;
});
</script>
